import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { isLoggedIn } from '../lib/auth'

/** /join/:token — the special link that binds a signed-in user into a Family Circle. */
export default function JoinPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [preview, setPreview] = useState<{ org_name: string; role: string; require_approval?: number; my_request_pending?: boolean } | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [pendingMsg, setPendingMsg] = useState('')

  useEffect(() => {
    if (!isLoggedIn()) {
      sessionStorage.setItem('fa_pending_join', token || '')
      navigate('/login')
      return
    }
    api.previewJoin(token || '')
      .then(setPreview)
      .catch(e => setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]/g, '').replace('error:', '') : 'Invalid invitation'))
  }, [token, navigate])

  const join = async () => {
    setBusy(true)
    try {
      const res = await api.joinOrg(token || '')
      if (res.pending) {
        setPendingMsg(res.message || 'Your request awaits approval from the circle keepers.')
        return
      }
      navigate('/circles', { state: { joined: res.org_name } })
    } catch (e) {
      setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Join failed')
    } finally { setBusy(false) }
  }

  const waiting = pendingMsg || preview?.my_request_pending

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {err ? (
        <>
          <div className="text-6xl mb-6">🥀</div>
          <h1 className="font-display text-2xl font-bold text-slate-100 mb-3">This invitation has faded</h1>
          <p className="text-sm text-slate-400 mb-8">{err}</p>
          <Link to="/dashboard" className="btn-ghost">Back to the archive</Link>
        </>
      ) : preview && waiting ? (
        <>
          <div className="text-6xl mb-6 float-slow inline-block">🕯️</div>
          <h1 className="font-display text-2xl font-bold text-slate-100 mb-3">
            Awaiting the keeper's blessing
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            {pendingMsg || `Your request to join ${preview.org_name} is with the circle's keepers. You'll receive an email once it's approved.`}
          </p>
          <Link to="/dashboard" className="btn-ghost">Back to the archive</Link>
        </>
      ) : preview ? (
        <>
          <div className="text-6xl mb-6 float-slow inline-block">🫂</div>
          <h1 className="font-display text-2xl font-bold text-slate-100 mb-3">
            You're invited to <span className="text-gradient-gold">{preview.org_name}</span>
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            Joining as <span className="text-gold-300 font-semibold">{preview.role}</span> — you'll see every tree this circle shares.
            {preview.require_approval ? ' A circle keeper will confirm your request.' : ''}
          </p>
          <button onClick={join} disabled={busy} className="btn-gold btn-shine !px-10 !py-3.5">
            {busy ? 'Joining…' : preview.require_approval ? 'Request to join the circle' : 'Take your place in the circle'}
          </button>
        </>
      ) : (
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400 mx-auto" />
      )}
    </div>
  )
}
